/**
 * PlanSelection — W2.6
 *
 * Ansicht für Nicht-Premium-User.
 * Plan-Cards + Dauer-Selector.
 * "Abonnieren"-Button öffnet CheckoutModal.
 * Mobile: vertikale Stack-Liste < 600px.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { useSubscriptionPlans } from './useSubscriptionPlans'
import { PlanCard } from './PlanCard'
import { DURATION_OPTIONS } from './subscriptionUtils'
import type { DurationMonths } from './subscriptionUtils'
import { CheckoutModal } from './CheckoutModal'
import type { Plan } from './useSubscriptionPlans'
import type { CryptoQuote, Subscription, SubscribeResult } from './useSubscription'

type PlanSelectionProps = {
  subscribe: (paymentMethodId: string, planId: string, durationMonths: number) => Promise<SubscribeResult>
  requestCryptoQuote: (planId: string, durationMonths: number) => Promise<CryptoQuote>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }>
  onSubscribeSuccess: () => void
  onAlreadySubscribed: () => void
  // Deep-Link ?plan=<id> aus /plans — wenn gültig, öffnet CheckoutModal
  // automatisch beim ersten Rendern. Unbekannte/fehlende ID = kein Effekt.
  initialPlanId?: string
  // Story 6.3: Laufzeit aus dem /plans-Umschalter (?duration=) vorbelegen.
  initialDuration?: DurationMonths
}

function SkeletonCard() {
  return (
    <div
      className="card skeleton"
      aria-hidden="true"
      style={{ padding: 24, minHeight: 200 }}
    >
      <div className="skeleton-line" style={{ width: '60%', height: 18, marginBottom: 8 }} />
      <div className="skeleton-line" style={{ width: '40%', height: 14, marginBottom: 16 }} />
      <div className="skeleton-line" style={{ width: '50%', height: 32, marginBottom: 16 }} />
      <div className="skeleton-line" style={{ width: '100%', height: 36 }} />
    </div>
  )
}

export function PlanSelection({
  subscribe,
  requestCryptoQuote,
  refetchSubscription,
  onSubscribeSuccess,
  onAlreadySubscribed,
  initialPlanId,
  initialDuration,
}: PlanSelectionProps) {
  const { t } = useTranslation('subscription')
  // F2 (Welle-4b): Anzeige UND Checkout muessen aus demselben Katalog kommen.
  // Ohne currency laed dieser Hook den USD-Katalog, waehrend /plans die
  // User-Waehrung anzeigt — die Karte zeigte dann EUR-Preise, der Checkout
  // buchte aber die USD-Stripe-Preise dieses Katalogs. Ein gemeinsamer
  // Hook-Aufruf liefert Preis, Waehrung und stripe_price_id aus EINER Quelle;
  // das ausgewaehlte Plan-Objekt geht unveraendert ins CheckoutModal.
  const { currency } = useCryptoPrices()
  const { plans, isLoading, isError, refetch } = useSubscriptionPlans(currency)
  const [duration, setDuration] = useState<DurationMonths>(initialDuration ?? 1)
  // Auto-Vorauswahl per Deep-Link: genau EINMAL pro Deep-Link konsumieren,
  // sobald die Plans geladen sind — ein reiner useState-Initializer verlor den
  // Deep-Link bei kaltem Seiten-Load (Race: Mount vor Abschluss des
  // Plans-Fetch). Der Consumed-Guard verhindert zugleich ein erneutes Öffnen
  // nach manuellem Schließen. Umsetzung als State-Anpassung WÄHREND des
  // Renderns (React-Doku "adjusting state when props change") statt
  // useEffect/useRef — beides verbietet die react-hooks-Lint-Konfiguration
  // (set-state-in-effect bzw. refs-during-render).
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [consumedDeepLink, setConsumedDeepLink] = useState<string | null>(null)
  if (initialPlanId && consumedDeepLink !== initialPlanId && plans?.length) {
    setConsumedDeepLink(initialPlanId)
    const plan = plans.find((p) => p.id === initialPlanId)
    if (plan) setSelectedPlan(plan)
  }

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label={t('planSelection.loading.ariaLabel')} data-testid="plans-loading">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div role="alert" className="empty-state" data-testid="plans-error">
        <p>{t('planSelection.error.text')}</p>
        <button className="btn btn-primary" onClick={refetch}>
          {t('common:actions.retry')}
        </button>
      </div>
    )
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ marginBottom: 8 }}>{t('planSelection.title')}</h2>
        <p style={{ color: 'var(--text-subtle)', fontSize: 14, marginBottom: 16 }}>
          {t('planSelection.subtitle')}
        </p>

        {/* Dauer-Selector */}
        <div
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
          role="group"
          aria-label={t('planSelection.durationAriaLabel')}
          data-testid="duration-selector"
        >
          {DURATION_OPTIONS.map((d) => (
            <button
              key={d}
              className={`btn ${duration === d ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setDuration(d)}
              aria-pressed={duration === d}
              data-testid={`duration-${d}`}
            >
              {t('duration.month', { count: d })}
            </button>
          ))}
        </div>
      </div>

      {/* Plan-Cards Grid — Mobile: stacked */}
      <div
        data-testid="plan-cards"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}
      >
        <style>{`
          @media (max-width: 600px) {
            [data-testid="plan-cards"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
        {(plans ?? []).map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            duration={duration}
            onSubscribe={setSelectedPlan}
          />
        ))}
      </div>

      {selectedPlan && (
        <CheckoutModal
          plan={selectedPlan}
          duration={duration}
          onSuccess={() => {
            setSelectedPlan(null)
            onSubscribeSuccess()
          }}
          onAlreadySubscribed={() => {
            setSelectedPlan(null)
            onAlreadySubscribed()
          }}
          onClose={() => setSelectedPlan(null)}
          subscribe={subscribe}
          requestCryptoQuote={requestCryptoQuote}
          refetchSubscription={refetchSubscription}
        />
      )}
    </>
  )
}
